<template>
  <b-modal
    id="modal-user-bundle-orders-import"
    size="lg"
    centered
    hide-footer
    :title="step === 'form' ? 'Import danh sách khách hàng' : step === 'preview' ? 'Xem trước kết quả' : 'Kết quả import'"
    @hidden="resetModal"
  >
    <b-overlay :show="loading">

      <!-- Step 1: Form upload -->
      <div v-if="step === 'form'">
        <div class="mb-4">
          <label class="font-weight-bold">Gói dịch vụ <span class="text-danger">*</span></label>
          <select v-model="selectedPackageId" class="form-control">
            <option :value="null" disabled>-- Chọn gói dịch vụ --</option>
            <option v-for="pkg in salePackages" :key="pkg.id" :value="pkg.id">
              {{ pkg.name }}
            </option>
          </select>
        </div>

        <div class="mb-4">
          <label class="font-weight-bold">File danh sách (.xlsx) <span class="text-danger">*</span></label>
          <div class="custom-file">
            <input
              ref="fileInput"
              type="file"
              class="custom-file-input"
              accept=".xlsx"
              @change="onFileChange"
            />
            <label class="custom-file-label">
              {{ importFile ? importFile.name : 'Chọn file Excel...' }}
            </label>
          </div>
          <small class="text-muted d-block mt-1">
            Chỉ chấp nhận file Excel (.xlsx, tối đa 5MB)
          </small>
        </div>

        <div class="mb-4">
          <a href="#" class="text-success" @click.prevent="downloadSample">
            <i class="fas fa-download mr-1"></i> Tải file mẫu
          </a>
        </div>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('modal-user-bundle-orders-import')">Hủy</b-button>
          <b-button variant="success" :disabled="!selectedPackageId || !importFile" @click="doDryRun">
            <i class="fas fa-eye mr-1"></i> Xem trước
          </b-button>
        </div>
      </div>

      <!-- Step 2: Preview (dry run result) -->
      <div v-if="step === 'preview'">
        <div class="row mb-4">
          <div class="col-3 text-center">
            <div class="stat-box">
              <div class="stat-value text-dark">{{ dryResult.total }}</div>
              <div class="stat-label">Tổng</div>
            </div>
          </div>
          <div class="col-3 text-center">
            <div class="stat-box">
              <div class="stat-value text-success">{{ dryResult.totalSuccess }}</div>
              <div class="stat-label">Sẽ kích hoạt</div>
            </div>
          </div>
          <div class="col-3 text-center">
            <div class="stat-box">
              <div class="stat-value text-warning">{{ dryResult.totalDuplicate }}</div>
              <div class="stat-label">Trùng lặp</div>
            </div>
          </div>
          <div class="col-3 text-center">
            <div class="stat-box">
              <div class="stat-value text-danger">{{ failRows.length }}</div>
              <div class="stat-label">Lỗi</div>
            </div>
          </div>
        </div>

        <!-- Duplicate handling -->
        <div v-if="dryResult.totalDuplicate > 0" class="mb-4">
          <h6 class="font-weight-bold mb-2">
            <i class="fas fa-exclamation-triangle text-warning mr-1"></i>
            Có {{ dryResult.totalDuplicate }} khách hàng trùng lặp
          </h6>
          <div class="border rounded mb-3" style="max-height:200px;overflow-y:auto;">
            <table class="table table-sm mb-0">
              <thead class="bg-light">
                <tr>
                  <th>Họ tên</th>
                  <th>SĐT</th>
                  <th>Email</th>
                  <th>Ngày hết hạn hiện tại</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(d, idx) in dryResult.duplicates" :key="idx">
                  <td>{{ d.fullName || '—' }}</td>
                  <td>{{ d.phone || '—' }}</td>
                  <td>{{ d.email || '—' }}</td>
                  <td>{{ formatUnixDate(d.existingEndDate) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <label class="font-weight-bold">Xử lý trùng lặp:</label>
          <div class="d-flex gap-3">
            <div class="custom-control custom-radio mr-4">
              <input
                id="dup-skip"
                v-model="duplicateAction"
                type="radio"
                class="custom-control-input"
                value="skip"
              />
              <label class="custom-control-label" for="dup-skip">Bỏ qua</label>
            </div>
            <div class="custom-control custom-radio">
              <input
                id="dup-extend"
                v-model="duplicateAction"
                type="radio"
                class="custom-control-input"
                value="extend"
              />
              <label class="custom-control-label" for="dup-extend">Gia hạn thêm 1 kỳ</label>
            </div>
          </div>
        </div>

        <!-- Per-row errors -->
        <div v-if="failRows.length > 0" class="mb-4">
          <h6 class="font-weight-bold mb-2">
            <i class="fas fa-times-circle text-danger mr-1"></i>
            Chi tiết lỗi
          </h6>
          <div class="border rounded" style="max-height:180px;overflow-y:auto;">
            <table class="table table-sm mb-0">
              <thead class="bg-light">
                <tr>
                  <th>Họ tên</th>
                  <th>SĐT</th>
                  <th>Email</th>
                  <th>Lỗi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in failRows" :key="idx">
                  <td>{{ row.fullName || '—' }}</td>
                  <td>{{ row.phone || '—' }}</td>
                  <td>{{ row.email || '—' }}</td>
                  <td class="text-danger">{{ row.errorMessage || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="step = 'form'">
            <i class="fas fa-arrow-left mr-1"></i> Quay lại
          </b-button>
          <b-button variant="success" @click="doImport" :disabled="dryResult.totalSuccess === 0 && dryResult.totalDuplicate === 0">
            <i class="fas fa-check mr-1"></i> Xác nhận import
          </b-button>
        </div>
      </div>

      <!-- Step 3: Final result -->
      <div v-if="step === 'result'">
        <div class="text-center mb-4">
          <i class="fas fa-check-circle text-success" style="font-size:2.5rem;"></i>
          <p class="mt-3 mb-1 font-weight-bold">Import hoàn tất</p>
        </div>

        <div class="row mb-4">
          <div class="col-4 text-center">
            <div class="stat-box">
              <div class="stat-value text-success">{{ finalResult.totalSuccess }}</div>
              <div class="stat-label">Thành công</div>
            </div>
          </div>
          <div class="col-4 text-center">
            <div class="stat-box">
              <div class="stat-value text-warning">{{ finalResult.totalDuplicate }}</div>
              <div class="stat-label">Trùng lặp</div>
            </div>
          </div>
          <div class="col-4 text-center">
            <div class="stat-box">
              <div class="stat-value text-danger">{{ finalFailCount }}</div>
              <div class="stat-label">Thất bại</div>
            </div>
          </div>
        </div>

        <div v-if="finalResult.data && finalResult.data.length" class="mb-4">
          <div class="border rounded" style="max-height:260px;overflow-y:auto;">
            <table class="table table-sm mb-0">
              <thead class="bg-light">
                <tr>
                  <th>Họ tên</th>
                  <th>SĐT</th>
                  <th>Email</th>
                  <th>Trạng thái</th>
                  <th>Ghi chú</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in finalResult.data" :key="idx">
                  <td>{{ row.fullName || '—' }}</td>
                  <td>{{ row.phone || '—' }}</td>
                  <td>{{ row.email || '—' }}</td>
                  <td>
                    <span v-if="row.transactionStatus && row.isDuplicate" class="text-success">Đã gia hạn</span>
                    <span v-else-if="row.transactionStatus" class="text-success">Thành công</span>
                    <span v-else-if="row.isDuplicate" class="text-warning">Bỏ qua (trùng)</span>
                    <span v-else class="text-danger">Thất bại</span>
                  </td>
                  <td class="text-muted">{{ row.errorMessage || '' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex justify-content-end">
          <b-button variant="success" @click="$bvModal.hide('modal-user-bundle-orders-import')">Đóng</b-button>
        </div>
      </div>

    </b-overlay>
  </b-modal>
</template>

<script>
export default {
  name: 'UserBundleOrdersImport',
  data() {
    return {
      loading: false,
      step: 'form', // 'form' | 'preview' | 'result'
      salePackages: [],
      selectedPackageId: null,
      importFile: null,
      duplicateAction: 'skip',
      dryResult: {
        total: 0,
        totalSuccess: 0,
        totalFail: 0,
        totalDuplicate: 0,
        data: [],
        duplicates: [],
      },
      finalResult: {
        total: 0,
        totalSuccess: 0,
        totalFail: 0,
        totalDuplicate: 0,
        data: [],
      },
    };
  },
  computed: {
    // transactionStatus is a boolean from the API. A skipped duplicate is isDuplicate && !transactionStatus
    // and is not an error; the API's totalFail counts it, so count real failures here.
    failRows() {
      return (this.dryResult.data || []).filter((r) => !r.transactionStatus && !r.isDuplicate);
    },
    finalFailCount() {
      return (this.finalResult.data || []).filter((r) => !r.transactionStatus && !r.isDuplicate).length;
    },
  },
  created() {
    this.loadSalePackages();
  },
  methods: {
    async loadSalePackages() {
      try {
        const res = await this.$api.get('SalePackage/All');
        const raw = res?.data ?? res;
        const list = Array.isArray(raw) ? raw : (raw?.data ?? raw?.items ?? []);
        this.salePackages = list.map((p) => ({ id: p.Id || p.id, name: p.Name || p.name }));
      } catch (e) {
        console.error('Cannot load sale packages:', e);
      }
    },

    onFileChange(e) {
      this.importFile = e.target.files[0] || null;
    },

    downloadSample() {
      // Build a minimal CSV-style content and rename to .xlsx as a placeholder.
      // A real xlsx binary requires a library; we generate a tab-separated text
      // wrapped inside a Blob so the browser downloads it as a recognisable file.
      const header = 'Họ tên\tSố điện thoại\tEmail';
      const rows = [
        'Nguyễn Văn A\t+84900990001\tnguyen.van.a@example.com',
        'Trần Thị B\t+84900990002\ttran.thi.b@example.com',
        'Lê Văn C\t+84900990003\tle.van.c@example.com',
      ];
      const content = [header, ...rows].join('\n');
      const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mau_import_khach_hang.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    // The API wraps the result as { meta, data: <BulkRegisterResult> } and the result itself has a
    // `data` array (rows). Unwrap exactly once, otherwise the rows array is mistaken for the result.
    unwrapBulkResult(res) {
      const d = res?.data ?? res;
      if (d && d.total === undefined && d.data && !Array.isArray(d.data)) return d.data;
      return d || {};
    },

    buildFormData(dryRun) {
      const fd = new FormData();
      fd.append('salePackageId', this.selectedPackageId);
      fd.append('importFile', this.importFile);
      fd.append('dryRun', dryRun ? 'true' : 'false');
      fd.append('duplicateAction', this.duplicateAction);
      return fd;
    },

    async doDryRun() {
      if (!this.selectedPackageId || !this.importFile) return;
      this.loading = true;
      try {
        const res = await this.$api.post('UserBundleOrders/register-bulk', this.buildFormData(true), {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const d = this.unwrapBulkResult(res);
        this.dryResult = {
          total: d.total ?? 0,
          totalSuccess: d.totalSuccess ?? 0,
          totalFail: d.totalFail ?? 0,
          totalDuplicate: d.totalDuplicate ?? 0,
          data: Array.isArray(d.data) ? d.data : [],
          duplicates: Array.isArray(d.duplicates) ? d.duplicates : [],
        };
        this.duplicateAction = 'skip';
        this.step = 'preview';
      } catch (e) {
        this.$toastr?.e?.({ title: 'Lỗi', msg: e?.message || 'Không thể xem trước danh sách.' });
      } finally {
        this.loading = false;
      }
    },

    async doImport() {
      this.loading = true;
      try {
        const res = await this.$api.post('UserBundleOrders/register-bulk', this.buildFormData(false), {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const d = this.unwrapBulkResult(res);
        this.finalResult = {
          total: d.total ?? 0,
          totalSuccess: d.totalSuccess ?? 0,
          totalFail: d.totalFail ?? 0,
          totalDuplicate: d.totalDuplicate ?? 0,
          data: Array.isArray(d.data) ? d.data : [],
        };
        this.step = 'result';
      } catch (e) {
        this.$toastr?.e?.({ title: 'Lỗi', msg: e?.message || 'Import thất bại. Vui lòng thử lại.' });
      } finally {
        this.loading = false;
      }
    },

    resetModal() {
      if (this.step === 'result') {
        this.$emit('success');
      }
      this.step = 'form';
      this.selectedPackageId = null;
      this.importFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      this.duplicateAction = 'skip';
      this.dryResult = { total: 0, totalSuccess: 0, totalFail: 0, totalDuplicate: 0, data: [], duplicates: [] };
      this.finalResult = { total: 0, totalSuccess: 0, totalFail: 0, totalDuplicate: 0, data: [] };
    },

    formatUnixDate(unix) {
      if (!unix) return '—';
      // existingEndDate is UNIX SECONDS
      const ms = unix < 1e10 ? unix * 1000 : unix;
      return this.$moment ? this.$moment(ms).format('D/M/YYYY') : new Date(ms).toLocaleDateString('vi-VN');
    },
  },
};
</script>

<style scoped>
.stat-box {
  background: #f9f9f9;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  padding: 12px;
}
.stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1;
}
.stat-label {
  font-size: 0.8rem;
  color: #7e8299;
  margin-top: 4px;
}
</style>
